import { Link } from "wouter";
import { ArrowRight, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";

type IssueAction = {
  label: string;
  href: string;
  variant?: "default" | "outline";
};

export function IssueScreen({
  badgeLabel,
  title,
  description,
  action,
  secondaryAction,
  sideTitle,
  sideEyebrow,
  sideStatus,
  sideDetails,
}: {
  badgeLabel: string;
  title: string;
  description: string;
  action?: IssueAction;
  secondaryAction?: IssueAction;
  sideTitle: string;
  sideEyebrow: string;
  sideStatus: string;
  sideDetails: string;
}) {
  return (
    <div className="min-h-screen bg-background px-6 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
        <div className="relative w-full overflow-hidden rounded border border-border/70 bg-card">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(204,255,0,0.12),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(184,134,11,0.16),transparent_36%)]" />
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/70 via-primary to-primary/60" />
          <div className="relative grid gap-10 p-8 md:grid-cols-[1.2fr_0.8fr] md:p-12">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.32em] text-primary">
                <ShieldAlert className="h-3.5 w-3.5" />
                {badgeLabel}
              </div>
              <div className="space-y-4">
                <h1 className="max-w-xl text-3xl font-display font-bold tracking-tight text-foreground md:text-5xl">
                  {title}
                </h1>
                <p className="max-w-2xl text-sm leading-7 text-muted-foreground md:text-base">{description}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {action ? (
                  <Button asChild className="gap-2">
                    <Link href={action.href}>
                      {action.label}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : null}
                {secondaryAction ? (
                  <Button asChild variant={secondaryAction.variant ?? "outline"}>
                    <Link href={secondaryAction.href}>{secondaryAction.label}</Link>
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="relative">
              <div className="rounded border border-border/70 bg-background/80 p-6 backdrop-blur">
                <div className="mb-5 flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-[0.28em] text-muted-foreground">
                    {sideEyebrow}
                  </span>
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded bg-destructive/10 text-destructive">
                    <ShieldAlert className="h-5 w-5" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="rounded border border-border bg-card p-4">
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-muted-foreground">
                      Etat
                    </div>
                    <div className="mt-2 text-lg font-semibold text-foreground">{sideStatus}</div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded border border-border bg-card p-4">
                      <div className="text-xs font-mono uppercase tracking-[0.24em] text-muted-foreground">
                        Contexte
                      </div>
                      <div className="mt-2 text-sm text-foreground">{sideTitle}</div>
                    </div>
                    <div className="rounded border border-border bg-card p-4">
                      <div className="text-xs font-mono uppercase tracking-[0.24em] text-muted-foreground">
                        Surface
                      </div>
                      <div className="mt-2 text-sm text-foreground">{badgeLabel}</div>
                    </div>
                  </div>
                  <div className="rounded border border-dashed border-primary/30 bg-primary/5 p-4 text-sm leading-6 text-muted-foreground">
                    {sideDetails}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
